import React, { useState, useEffect, useRef } from 'react';
import { useSettings } from '../../../hooks/useSettings';
import { useAuth } from '../../../hooks/useAuth';
import { usePreferences } from '../../../hooks/usePreferences';
import '../../../styles/SettingsPage.css';

export const SettingsPage: React.FC = () => {
  const { settings, saveSettings: updateSettings, resetSettings, isLoading, error } = useSettings();
  const { user } = useAuth();
  const { preferences, changeNavigationLayout } = usePreferences();
  const [activeTab, setActiveTab] = useState('navigation'); // Changed from 'theme' to 'navigation'
  const [navigationLayout, setNavigationLayout] = useState<
    'header' | 'sidebar'
  >('sidebar');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const securityInitialized = useRef(false);

  // Show message and auto-clear after 3 seconds
  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  useEffect(() => {
    let isMounted = true;
    
    // Load navigation layout preference from Redux
    if (preferences?.navigationLayout) {
      setNavigationLayout(preferences.navigationLayout);
    } else if (isMounted && user) {
      // Default to sidebar layout for better UX (only if user is logged in)
      setNavigationLayout('sidebar');
      changeNavigationLayout('sidebar').catch(err => {
        // Ignore errors if component unmounted or user logged out
        if (!isMounted) return;
        console.error('Failed to set default navigation layout:', err);
      });
    }
    
    // Cleanup function to prevent state updates after unmount (e.g., during logout)
    return () => {
      isMounted = false;
      securityInitialized.current = false;
    };
  }, [preferences, changeNavigationLayout, user]);

  // Initialize security settings in useEffect to prevent updates during logout
  useEffect(() => {
    let isMounted = true;
    
    if (settings && !settings.security && updateSettings && !securityInitialized.current && user) {
      securityInitialized.current = true;
      // Use setTimeout to avoid state updates during render
      const timeoutId = setTimeout(() => {
        // Only update if component is still mounted and user is still logged in
        if (isMounted && user) {
          updateSettings({
            security: {
              idleTimeout: 2,
              enableIdleTimeout: true,
            },
          }).catch(err => {
            // Ignore errors if component unmounted or user logged out
            if (!isMounted) return;
            console.error('Failed to initialize security settings:', err);
          });
        }
      }, 0);
      
      // Cleanup timeout on component unmount (e.g., during logout)
      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
      };
    }
  }, [settings, updateSettings, user]);

  if (isLoading || !settings) {
    return (
      <div className="settings-page">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  // HIDDEN: Theme change handler removed - Keep code for future reuse
  // const handleThemeChange = async (theme: 'light' | 'dark') => {
  //   if (!updateSettings) return;
  //   try {
  //     await updateSettings({ theme });
  //     showMessage('success', 'Theme updated successfully');
  //   } catch (error) {
  //     showMessage('error', 'Failed to update theme');
  //   }
  // };

  const handleDisplayChange = async (
    key: keyof typeof settings.display,
    value: any
  ) => {
    if (!settings || !updateSettings) return;
    try {
      await updateSettings({
        display: {
          ...settings.display,
          [key]: value,
        },
      });
      showMessage('success', 'Display settings updated successfully');
    } catch (error) {
      showMessage('error', 'Failed to update display settings');
    }
  };


  const handleSecurityChange = async (
    key: keyof typeof settings.security,
    value: any
  ) => {
    if (!settings || !updateSettings) {
      return;
    }
    try {
      const result = await updateSettings({
        security: {
          ...(settings?.security || {}),
          [key]: value,
        },
      });
      showMessage('success', 'Security settings updated successfully');
    } catch (error) {
      console.error('updateSettings error:', error);
      showMessage('error', 'Failed to update security settings');
    }
  };

  const handleNavigationLayoutChange = async (layout: 'header' | 'sidebar') => {
    // Check if user is still logged in
    if (!user) {
      console.warn('User not logged in, cannot change navigation layout');
      return;
    }
    
    try {
      console.log('🔄 Changing navigation layout to:', layout);
      setNavigationLayout(layout);
      
      console.log('🔄 Calling changeNavigationLayout...');
      const result = await changeNavigationLayout(layout);
      console.log('✅ changeNavigationLayout result:', result);

      // Dispatch custom event to notify App.tsx
      window.dispatchEvent(
        new CustomEvent('navigationLayoutChange', {
          detail: { layout },
        })
      );

      // Show success message
      setMessage({
        type: 'success',
        text: `Navigation layout changed to ${layout === 'header' ? 'Header' : 'Sidebar'} successfully!`
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('❌ Failed to change navigation layout:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save navigation layout preference. Please try again.'
      });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const tabs = [
    // HIDDEN: Theme tab removed - Keep code for future reuse
    // { id: 'theme', label: 'Theme & Appearance', icon: '🎨' },
    { id: 'navigation', label: 'Navigation', icon: '🧭' },
    { id: 'display', label: 'Display', icon: '👁️' },
    { id: 'privacy', label: 'Privacy & Security', icon: '🔒' },
  ];

  // HIDDEN: Dark mode disabled - Keep code for future reuse
  // const isDarkMode = settings.theme === 'dark';
  const isDarkMode = false;

  return (
    <div className={`settings-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Customize your MPM Medical experience</p>
      </div>

      {/* Success/Error Message */}
      {message && (
        <div className={`message ${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="settings-content">
          {/* HIDDEN: Theme tab content removed - Keep code for future reuse */}
          {/* {activeTab === 'theme' && (
            <div className="settings-section">
              <h2>Theme & Appearance</h2>
              <div className="setting-group">
                <label className="setting-label">Theme Mode</label>
                <div className="theme-options">
                  <button
                    className={`theme-option ${settings.theme === 'light' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('light')}
                  >
                    <span className="theme-icon">☀️</span>
                    <span>Light</span>
                  </button>
                  <button
                    className={`theme-option ${settings.theme === 'dark' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('dark')}
                  >
                    <span className="theme-icon">🌙</span>
                    <span>Dark</span>
                  </button>
                </div>
                <p className="setting-description">
                  Choose your preferred theme. Light mode provides a bright
                  interface, while dark mode offers a comfortable viewing
                  experience.
                </p>
              </div>
            </div>
          )} */}

          {activeTab === 'navigation' && (
            <div className="settings-section">
              <h2>Navigation Layout</h2>
              <div className="setting-group">
                <label className="setting-label">Navigation Style</label>
                <div className="layout-options">
                  <button
                    className={`layout-option ${navigationLayout === 'sidebar' ? 'active' : ''}`}
                    onClick={() => handleNavigationLayoutChange('sidebar')}
                  >
                    <span className="layout-icon">📱</span>
                    <span>Sidebar Navigation</span>
                    <span className="layout-description">
                      Left sidebar with navigation menu
                    </span>
                  </button>
                  <button
                    className={`layout-option ${navigationLayout === 'header' ? 'active' : ''}`}
                    onClick={() => handleNavigationLayoutChange('header')}
                  >
                    <span className="layout-icon">🖥️</span>
                    <span>Header Navigation</span>
                    <span className="layout-description">
                      Top header with navigation links
                    </span>
                  </button>
                </div>
                <p className="setting-description">
                  Choose your preferred navigation layout. Sidebar provides more
                  space for navigation items, while header keeps the interface
                  compact.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'display' && (
            <div className="settings-section">
              <h2>Display Settings</h2>
              <div className="setting-group">
                <label className="setting-label">Compact Mode</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="compact-mode"
                    checked={settings.display.compactMode}
                    onChange={e =>
                      handleDisplayChange('compactMode', e.target.checked)
                    }
                  />
                  <label
                    htmlFor="compact-mode"
                    className="toggle-label"
                  ></label>
                </div>
                <p className="setting-description">
                  Reduce spacing for a more compact interface
                </p>
              </div>


              <div className="setting-group">
                <label className="setting-label">Font Size</label>
                <div className="font-size-preview">
                  <span
                    className={`preview-text font-size-${settings.display.fontSize}`}
                  >
                    Preview: This is how your text will look
                  </span>
                </div>
                <div className="font-size-options">
                  {(['small', 'medium', 'large'/* , 'extra-large' */] as const).map(size => (
                    <button
                      key={size}
                      className={`font-size-option ${settings.display.fontSize === size ? 'active' : ''} font-size-${size}`}
                      onClick={() => handleDisplayChange('fontSize', size)}
                    >
                      {size.charAt(0).toUpperCase() + size.slice(1).replace('-', ' ')}
                    </button>
                  ))}
                </div>
                <p className="setting-description">
                  Adjust the text size for better readability
                </p>
              </div>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="settings-section">
              <h2>Privacy & Security</h2>
              
              <div className="setting-group">
                <label className="setting-label">Enable Idle Timeout</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="enable-idle-timeout"
                    checked={settings.security.enableIdleTimeout}
                    onChange={e =>
                      handleSecurityChange('enableIdleTimeout', e.target.checked)
                    }
                  />
                  <label
                    htmlFor="enable-idle-timeout"
                    className="toggle-label"
                  ></label>
                </div>
                <p className="setting-description">
                  Automatically log out after a period of inactivity for security
                </p>
              </div>


            </div>
          )}
        </div>
      </div>

      <div className="settings-actions">
        <button onClick={resetSettings} className="reset-button">
          Reset to Default
        </button>
        <div className="settings-info">
          <p>
            Your settings are automatically saved and will be restored on your
            next login.
          </p>
        </div>
      </div>
    </div>
  );
};
